















































































































































































































import {
	computed,
	defineComponent,
	nextTick,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import getContrast from 'get-contrast';
import { v } from '@front-app/components/form/validation/useValidation';
import { requiredIfBoth } from '@front-app/components/form/validation/validators';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { useCommunityColors } from '@front-app/modules/community/hooks/useCommunityColors';
import { updateCommunity } from '@front-app/services/CommunityApi/CommunityApi';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { CommunityWithGroups, CommunityColourPalette } from '@front-app/services/CommunityApi/CommunityModel';
import { Size, Spacing } from '@front-app/components/constants';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import HelpTooltipBlock from '@front-app/components/dropdowns/HelpTooltipBlock.vue';
import EditorWrapper from '@front-app/components/layouts/form/EditorWrapper.vue';
import cloneDeep from 'clone-deep';
import BaseButtonRender from '@front-app/modules/group/highlights/components/Button/actions/BaseButtonRender.vue';
import { HighlightsButtonStyle, HighlightsElementPalette } from '@front-app/modules/group/highlights/model';
import { DISCUSSIONS_ICON } from '@front-app/vendor/icon-definitions';
import ColorPicker from '@front-app/components/inputs/ColorPicker.vue';
import { STANDARD_CONTRAST_RATIO } from '@front-app/util/color';
import { sleep } from '@front-app/util/async';
import TabbedNav from '@front-app/components/tabs/TabbedNav.vue';
import { ButtonNavButton } from '@front-app/components/buttons/buttonNav';
import { Routes } from '@front-app/constants/routes';

const FAKE_TAB_BUTTONS: ButtonNavButton[] = [
	{
		key: '1',
		label: 'Selected Tab',
		to: { name: Routes.MANAGE_COMMUNITY_SETTINGS },
	},
	{
		key: '2',
		label: 'Sample Tab',
		to: { name: Routes.MANAGE_COMMUNITY_SETTINGS },
	},
	{
		key: '3',
		label: 'Other Tab',
		to: { name: Routes.MANAGE_COMMUNITY_SETTINGS },
	},
];

export default defineComponent({
	components: {
		FormSection,
		FormLabel,
		FormError,
		HelpTooltipBlock,
		EditorWrapper,
		BaseButtonRender,
		ColorPicker,
		TabbedNav,
	},
	props: {
		community: {
			type: Object as PropType<CommunityWithGroups>,
			required: false,
			default: null,
		},
		isOpen: Boolean,
	},
	setup(props) {
		const palette: Ref<CommunityColourPalette> = ref({
			primary: '',
			secondary: [],
		});
		watch(() => props.community.customColorPalette, () => {
			if (!props.community.customColorPalette) {
				palette.value = { primary: '', secondary: [] };
			} else {
				palette.value = cloneDeep(props.community.customColorPalette);
			}
		}, { immediate: true });

		const hasSufficientContrast = async (val: string, selector: string): Promise<boolean> => {
			if (!val) {
				return true;
			}

			await nextTick();
			await sleep(100);

			const el = document.querySelector(selector);
			if (!el) {
				return true;
			}
			const { color, backgroundColor } = window.getComputedStyle(el);
			return getContrast.ratio(color, backgroundColor) >= STANDARD_CONTRAST_RATIO;
		};

		const isPrimaryContrastSufficient = ref(true);
		const setPrimaryColor = async (val: string) => {
			palette.value.primary = val;

			isPrimaryContrastSufficient.value = await hasSufficientContrast(val, '.primary-filled');
		};
		const isSecondaryContrastSufficient = ref([true, true, true]);
		const setSecondaryColor = async (idx: number, val: string) => {
			const { secondary } = palette.value;
			secondary[idx] = val;
			palette.value.secondary = [...secondary];

			const newVal = [...isSecondaryContrastSufficient.value];
			newVal[idx] = await hasSufficientContrast(val, `.secondary${idx}-filled`);
			isSecondaryContrastSufficient.value = newVal;
		};

		const getSecondaryColor = (idx: number) => palette.value.secondary[idx];

		const hasAnySecondaryPalette = computed(() => palette.value.secondary.some(Boolean));
		const validation = v({
			primary: {
				value: computed(() => palette.value.primary),
				rules: [requiredIfBoth(() => hasAnySecondaryPalette.value)],
			},
		});

		const {
			track,
			isLoading,
			isError,
			isSuccess,
		} = useAsyncStatus();

		const {
			focusFirstInvalid,
		} = useFocusFirstInvalidField({ containerSelector: '.community__create-form' });

		const sortPalettes = ({ primary, secondary }: CommunityColourPalette): CommunityColourPalette => {
			const newSecondary = [...secondary].sort((a, b) => {
				if (a && b) {
					return 0;
				}
				if (b) {
					return 1;
				}
				return -1;
			});
			return {
				primary,
				secondary: newSecondary,
			};
		};

		const saveCommunity = async () => {
			validation.validate();
			if (!validation.isValid.value) {
				focusFirstInvalid();
				return;
			}
			const fixedPalettes = sortPalettes(palette.value);
			await track(updateCommunity(props.community.id, {
				customColorPalette: fixedPalettes,
			}));
		};

		const {
			headerNavStyles,
			highlightButtonStyles,
		} = useCommunityColors(palette);

		return {
			validation,
			hasAnySecondaryPalette,
			palette,
			saveCommunity,
			setPrimaryColor,
			setSecondaryColor,
			getSecondaryColor,
			// Contrast
			isPrimaryContrastSufficient,
			isSecondaryContrastSufficient,
			// State
			isLoading,
			isError,
			isSuccess,
			// Preview
			headerNavStyles,
			highlightButtonStyles,
			FAKE_TAB_BUTTONS,
			// Util
			DISCUSSIONS_ICON,
			Spacing,
			Size,
			HighlightsButtonStyle,
			HighlightsElementPalette,
		};
	},
});
